import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import type { RelationshipDto, ActiveWarDto } from '../../types/diplomacy';
import { HostilityBar } from './HostilityBar';
import { AT_WAR_COLOR } from './relationsPresentation';

interface Props {
  relationships: RelationshipDto[];
  activeWar: ActiveWarDto | null;
  layout: 'table' | 'stacked';
}

export function RelationsTable({ relationships, activeWar, layout }: Props) {
  const navigate = useNavigate();

  if (relationships.length === 0) {
    return (
      <p style={{ color: COLORS.text.tertiary, fontSize: 13 }}>
        No notable relationships — all kingdoms are Normal.
      </p>
    );
  }

  const kingdomLink = (kingdomId: string, kingdomName: string | null) => (
    <button
      onClick={() => navigate(`/world/dossier/${kingdomId}`)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: COLORS.link,
        cursor: 'pointer',
        fontSize: 13,
        textAlign: 'left',
      }}
    >
      {kingdomName ?? kingdomId} ({kingdomId})
    </button>
  );

  // SF-158 (Phase 3 Decision 2): an at-war row shows a single "At War" badge instead of hostility
  // bars — DeclareWarAction zeroes both hostility entries on declaration, so bars would render a
  // misleading "Normal" state for the entire duration of the war otherwise.
  const statusBlock = (r: RelationshipDto) => {
    const atWar = activeWar?.opponentKingdomId === r.kingdomId;
    if (atWar) {
      return (
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 700,
            color: AT_WAR_COLOR,
            border: `1px solid ${AT_WAR_COLOR}`,
          }}
        >
          At War
        </span>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 120 }}>
        <HostilityBar label="Our hostility" value={r.ourHostilityPoints} />
        <HostilityBar label="Their hostility" value={r.theirHostilityPoints} />
      </div>
    );
  };

  if (layout === 'stacked') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {relationships.map((r) => (
          <div
            key={r.kingdomId}
            style={{
              padding: 12,
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
            }}
          >
            <div style={{ marginBottom: 8 }}>{kingdomLink(r.kingdomId, r.kingdomName)}</div>
            {statusBlock(r)}
          </div>
        ))}
      </div>
    );
  }

  const thStyle: React.CSSProperties = {
    padding: '6px 8px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.text.secondary,
    backgroundColor: COLORS.sectionHeader,
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={thStyle}>Kingdom</th>
            <th style={thStyle}>Hostility</th>
          </tr>
        </thead>
        <tbody>
          {relationships.map((r, i) => (
            <tr
              key={r.kingdomId}
              style={{ backgroundColor: i % 2 === 1 ? COLORS.tableRowAlt : undefined }}
            >
              <td style={{ padding: '6px 8px', color: COLORS.text.primary }}>
                {kingdomLink(r.kingdomId, r.kingdomName)}
              </td>
              <td style={{ padding: '6px 8px' }}>{statusBlock(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
